import React, { useEffect, useRef, useState } from "react";
import {
  Grid,
  Button,
  Typography,
  Drawer,
  IconButton,
  Box,
  Switch,
  ButtonGroup,
  Stack,
  styled,
} from "@mui/material";
import {
  ArrowForwardIos,
  SettingsBackupRestore,
  Apps as GridIcon,
  CheckCircle,
} from "@mui/icons-material";
import { URLS } from "../../../constants";
import { uvenuFetcher } from "../../../utils/uvenu-fetcher";
import UserPreviewView from "./UserPreviewView";
import { insights } from "../../../ApplicationInsightsService";
import Loader from "../../../components/Loader/Loader";
import "../styles.css";
import { globals } from "../globals";
import { liveUsersGlobalState } from "./liveUsersGlobalState";
import { COLORS } from "../../../utils/colors";

export function RenderOnlineUsers({
  event,
  usersType,
  onUserSelected,
  onUserBlocked,
  userCounts,
  allowUsersData,
}) {
  const [gridView, setGridView] = useState("grid3x3");
  const [isLiveUser, setIsLiveUser] = useState("");
  const [perPage, setPerPage] = useState(9); // Number of users per page

  const [onlineUsers, setOnlineUsers] = useState();
  const [activeUsers, setActiveUsers] = useState([]);
  const [lastEventUserId, setLastEventUserId] = useState("");
  const [userLoading, setUserLoading] = useState(false);
  const [loading, setLoading] = useState(false);

  let selectedTab = 0;

  const [activeTab, setActiveTab] = useState(0);
  useEffect(() => {
    setActiveUsers([]);
    setActiveTab(usersType === "live" ? 0 : 2);
  }, [usersType]);

  useEffect(() => {
    getOnlineUsers(activeTab);
    return () => {
      liveUsersGlobalState.currentPreviewUserId = "";
    };
  }, [activeTab, perPage]);

  useEffect(() => {
    let intervalId;

    if (onlineUsers && onlineUsers?.json?.onlineUsers.length > 0) {
      setUserLoading(true);
      setActiveUsers(onlineUsers?.json?.onlineUsers);
    } else {
      // Call getOnlineUsers every 10 seconds
      intervalId = setInterval(() => {
        getOnlineUsers(activeTab);
      }, 10000);
      setActiveUsers([]);
      setLastEventUserId("");
    }
    globals.updateUserCounts();
    setUserLoading(false);
    // Clean up the interval when the condition is met or the component is unmounted
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [onlineUsers]);

  // Whenever the users change, detect if the current user
  // is not in there then close the pc and set noUserSelected.
  useEffect(() => {
    if (
      onlineUsers?.json?.totalOnlineUserCount > perPage &&
      activeUsers.length === perPage
    ) {
      setLastEventUserId(activeUsers[activeUsers.length - 1].eventUserId);
    }
    const userExists =
      activeUsers &&
      activeUsers.length &&
      activeUsers.find(
        (u) => u.eventUserId === liveUsersGlobalState.currentPreviewUserId
      );
    if (!userExists) {
      if (tabChangeOperation.current === true) {
        tabChangeOperation.current = false;
      } else {
        // If the current live user was also the current user.
        // Then we need to disconnect the screen too.
        if (
          liveUsersGlobalState.currentPreviewUserId != "" &&
          liveUsersGlobalState.currentOutputUserId ===
            liveUsersGlobalState.currentPreviewUserId
        ) {
          liveUsersGlobalState.currentOutputUserId = "";
          liveUsersGlobalState.sendCommandToMainScreen(event._id, `none`);
        }
      }
      liveUsersGlobalState.currentPreviewUserId = "";
      liveUsersGlobalState.reloadPreview({
        noUserSelection: true,
        loadingStatus: false,
      });
    }
    const ongoing_live_user_id = localStorage.getItem("ongoing_live_user_id");
    if (activeUsers.length > 0) {
      if (activeUsers.find((u) => u.eventUserId === ongoing_live_user_id)) {
        setIsLiveUser(ongoing_live_user_id);
        liveUsersGlobalState.currentOutputUserId = ongoing_live_user_id;
      }
    }
  }, [activeUsers]);

  useEffect(() => {
    if (gridView === "grid3x3") {
      setPerPage(9);
    } else {
      setPerPage(12);
    }
  }, [gridView]);

  const getOnlineUsers = async (selection, LastEventUserId = "") => {
    setLoading(true);
    selectedTab = activeTab;
    if (selectedTab !== selection) {
      setOnlineUsers();
      setActiveUsers([]);
      setLastEventUserId("");
    }

    const sourceTypes = ["geo", "vip", "production"];
    const sourceName = sourceTypes[selection];
    try {
      if (!navigator.onLine) return;
      let onlineUsers = await uvenuFetcher({
        method: "GET",
        url: URLS.GET_ONLINE_USERS(
          event._id,
          sourceName,
          LastEventUserId,
          perPage
        ),
      });
      setOnlineUsers(onlineUsers);
      setLoading(false);
    } catch (error) {
      insights.trackUvenuException(error, {
        status: "error",
        info: "Exception in getting online users list",
        error: error,
        adminSessionId: globals.adminSessionId,
      });
    }
  };

  const OnResetCLick = () => {
    liveUsersGlobalState.currentPreviewUserId = "";
    tabChangeOperation.current = true;
    setUserLoading(true);
    setOnlineUsers();
    setActiveUsers([]);
    setLastEventUserId("");
    getOnlineUsers(activeTab);
  };

  const OnNextCLick = () => {
    liveUsersGlobalState.currentPreviewUserId = "";
    tabChangeOperation.current = true;
    setUserLoading(true);
    getOnlineUsers(activeTab, lastEventUserId);
  };

  const OnRefreshClick = () => {
    liveUsersGlobalState.currentPreviewUserId = "";
    liveUsersGlobalState.reloadPreview({
      noUserSelection: true,
      loadingStatus: false,
    });
    setUserLoading(true);
    if (lastEventUserId) getOnlineUsers(activeTab, lastEventUserId);
    else getOnlineUsers(activeTab);
  };

  liveUsersGlobalState.updateLiveUserStatus = (liveUserId) => {
    setIsLiveUser("");
    liveUsersGlobalState.currentOutputUserId = "";
    if (liveUserId === liveUsersGlobalState.currentPreviewUserId) {
      liveUsersGlobalState.currentPreviewUserId = "";
      liveUsersGlobalState.reloadPreview({
        noUserSelection: true,
        loadingStatus: false,
      });
    }
  };

  liveUsersGlobalState.onCurrentStreamChanged = (userId) => {
    setIsLiveUser(userId);
    localStorage.setItem("ongoing_live_user_id", userId);
  };

  const handleGridViewChange = (newGridView) => {
    setGridView(newGridView);
  };

  let tabChangeOperation = useRef(false);
  const handleTabChange = (newValue) => {
    liveUsersGlobalState.currentPreviewUserId = "";
    setActiveTab(newValue);
    tabChangeOperation.current = true;
    getOnlineUsers(newValue);
  };

  const AntSwitch = styled(Switch)(({ theme }) => ({
    width: 42,
    height: 24,
    padding: 0,
    display: "flex",
    "&:active": {
      "& .MuiSwitch-thumb": {
        width: 22.5,
      },
      "& .MuiSwitch-switchBase.Mui-checked": {
        transform: "translateX(9px)",
        "& + .MuiSwitch-track": {
          opacity: 1,
          backgroundColor: "rgba(36, 184, 231, 0.5)",
        },
      },
    },
    "& .MuiSwitch-switchBase": {
      padding: 3,
      "&.Mui-checked": {
        transform: "translateX(18px)",
        color: "#fff",
      },
    },
    "& .MuiSwitch-thumb": {
      boxShadow: "0 2px 4px 0 rgb(0 35 11 / 20%)",
      width: 18,
      height: 18,
      borderRadius: 9,
    },
    "& .MuiSwitch-track": {
      borderRadius: 24 / 2,
      opacity: 1,
      backgroundColor: "#24B8E7",
      boxSizing: "border-box",
    },
  }));

  return (
    <div id="drawer-container" className="rouDrawerOuterDiv">
      <div className="light-show-home">
        <div className="console">
          <div className="show-list-container">
            {usersType === "live" && (
              <div className="header grey-card">
                <div className="actions">
                  <div className="total">
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                    >
                      <div
                        className="usersDot"
                        style={{ backgroundColor: "green" }}
                      />
                      <Typography
                        className="userConnectionText"
                        style={{ color: COLORS.textHeader, fontSize: "0.9rem" }}
                      >
                        Live Users: {activeUsers?.length || 0}
                      </Typography>
                    </div>
                  </div>
                  <ButtonGroup
                    sx={{ mt: 1, mb: 1, marginRight: "20px" }}
                    disableElevation
                    variant="contained"
                    aria-label="Disabled button group"
                    size="small"
                  >
                    <Button
                      variant="contained"
                      disableElevation
                      className="add-light-show-button"
                      onClick={() => {
                        handleTabChange(0);
                      }}
                      startIcon={activeTab === 0 ? <CheckCircle /> : null}
                    >
                      GEO
                    </Button>
                    <Button
                      variant="contained"
                      disableElevation
                      className="add-light-show-button"
                      onClick={() => {
                        handleTabChange(1);
                      }}
                      startIcon={activeTab === 1 ? <CheckCircle /> : null}
                    >
                      VIP
                    </Button>
                  </ButtonGroup>

                  <Stack
                    direction="row"
                    spacing={1}
                    sx={{ marginRight: "110px" }}
                    className="switchStack"
                  >
                    <Typography
                      style={{ color: COLORS.textHeader, fontSize: "0.9rem" }}
                    >
                      Allow Users: On
                    </Typography>
                    <AntSwitch
                      size="small"
                      onChange={() =>
                        allowUsersData.allowUsersStatusChanged(
                          !allowUsersData.checkedStatus
                        )
                      }
                      checked={!allowUsersData.checkedStatus}
                      inputProps={{ "aria-label": "ant design" }}
                    />
                    <Typography>Off</Typography>
                  </Stack>
                </div>
              </div>
            )}

            <div className="list grey-card">
              {activeUsers?.length > 0 && (
                <Drawer
                  className="rouDrawer"
                  open={true}
                  PaperProps={{
                    style: {
                      position: "absolute",
                      width: "100%",
                      backgroundColor: "transparent",
                    },
                  }}
                  BackdropProps={{ style: { position: "absolute" } }}
                  ModalProps={{
                    container: document.getElementById("drawer-container"),
                    style: {
                      position: "absolute",
                      marginTop: usersType === "live" ? 60 : 0,
                      backgroundColor: "transparent",
                    },
                  }}
                  anchor="left"
                >
                  {activeUsers?.length > 0 && (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        width:
                          usersType === "live" ? "100%" : "calc(100% - 129px)",
                      }}
                    >
                      <Typography
                        className="usersText"
                        style={{ color: COLORS.textHeader }}
                      >
                        USERS
                      </Typography>
                      <div></div>
                    </div>
                  )}
                  {activeUsers?.length > 0 && activeUsers ? (
                    loading ? (
                      <div
                        style={{ position: "absolute", alignSelf: "center" }}
                      >
                        <Loader />
                      </div>
                    ) : (
                      <Grid container spacing={1.5} padding={1.5}>
                        {activeUsers.map((usr) => (
                          <Grid
                            item
                            xs={gridView === "grid3x3" ? 4 : 3}
                            key={usr.eventUserId}
                          >
                            <UserPreviewView
                              key={usr.eventUserId}
                              event={event}
                              user={usr}
                              handleClicked={() => {
                                onUserSelected(usr);
                              }}
                              updateStream={() => {
                                onUserSelected(usr);
                              }}
                            />
                          </Grid>
                        ))}
                      </Grid>
                    )
                  ) : (
                    <p style={{ textAlign: "center", color: "white" }}>
                      Users on the page are not active anymore!
                    </p>
                  )}
                </Drawer>
              )}
            </div>
          </div>
        </div>

        <div
          className="bottomPaginationStrip"
          style={{ backgroundColor: COLORS.previewViewBackground }}
        >
          <div className="pagination">
            <IconButton
              onClick={OnResetCLick}
              size="small"
              sx={{ paddingRight: "0px" }}
            >
              <SettingsBackupRestore />
            </IconButton>
            {activeUsers?.length === perPage ? (
              <IconButton
                disabled={userLoading}
                onClick={OnNextCLick}
                size="medium"
                sx={{ fontSize: "1rem" }}
              >
                Next
                <ArrowForwardIos />
              </IconButton>
            ) : (
              <IconButton
                disabled={userLoading}
                onClick={OnRefreshClick}
                size="small"
                sx={{ fontSize: "0.9rem" }}
              >
                Refresh
              </IconButton>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
